import { useEffect, useRef, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { WaitState } from "../components/WaitState";
import { ResponseBlockedHint, type PortalResponse } from "../components/ResponseStep";
import { portalPackagesApi } from "../api/packages";
import type { ESignStartResult, PortalItemDto } from "../api/types";
import { signingRouteFor } from "../lib/signingRoute";
import { InHouseSigningSurface } from "./InHouseSigningSurface";
import { ApprovalChainItemSurface } from "./ApprovalChainItemSurface";
import { ApprovalChainProgress, ChainDoneCard } from "../components/ApprovalChainPanels";

// External Intake & Collection v1.0 PR 8 §9 — ESign fulfilment surface.
// Routing follows the server-enforced route (signingRouteFor): InHouse
// delegates to InHouseSigningSurface (the in-house signing module);
// otherwise the recipient presses "Start signing", we POST /esign/start and
// embed the returned recipient-view URL in an iframe. Opening the item never
// starts an envelope (A16) — that is always the recipient's click, and an
// item that owes a response asks for the note first (A14), sending it with
// the start. The "signature to be applied" PR-4 marker is gone — both paths
// now finalize into the PR-7 staging queue.
//
// Return flow: DocuSign redirects the signing IFRAME to the static
// break-out page (public/esign-return.html), which posts DocuSign's
// ?event= back here via same-origin postMessage. This component tears the
// iframe down and branches per event; the Sign → Review item transition
// itself is driven ONLY by the DocuSign Connect webhook — after
// signing_complete we poll the package until the webhook lands, and claim
// "Signature received" only once the item's status says so (A20).

// Message contract with public/esign-return.html. Keep the two in sync.
export const ESIGN_RETURN_MESSAGE_SOURCE = "phx-esign-return";

export const FINALIZE_POLL_INTERVAL_MS = 2_500;
export const FINALIZE_POLL_CAP_MS = 45_000;

/** Approval chains (D10) — a DocuSign decline on a chain stage IS a return:
 *  the server records it as one and the chain stops until staff revise it. */
export const CHAIN_DECLINE_DETAIL =
  "Your decline has been sent to Phoenix as a return; the document stops here until it is revised.";

/** Whether the caller's signature has landed: a chain item reads the caller's
 *  own chain status (the item status says nothing about one party's stage);
 *  any other item reads its status, which the Connect webhook flips. */
function signatureLanded(item: PortalItemDto): boolean {
  if (item.approvalChain) return item.approvalChain.myStatus === "Done";
  return item.status === "SubmittedForReview" || item.status === "Approved";
}

/** Shown after DocuSign reports signing_complete until the item's status
 *  confirms receipt — including after the poll cap. Claims nothing. */
export const FINISHING_UP_DETAIL =
  "This can take a minute. You can leave this page; we'll update your checklist when it's done.";

interface ESignItemSurfaceProps {
  item: PortalItemDto;
  /** Route packageId — the DocuSign branch polls the package after the
   *  ceremony returns (PortalItemDto carries no packageId). */
  packageId: string;
  onUpdated?: (next: PortalItemDto) => void;
  /** Review loop — set while the item owes a response; the note is sent with
   *  the DocuSign start (or the in-house signature). */
  response?: PortalResponse | null;
  /** In-house route only: the signature POST is in flight. */
  onSigningBusyChange?: (busy: boolean) => void;
  /** Approval chains, in-house route — re-read the item after the step lands. */
  onReload?: () => void | Promise<void>;
}

export function ESignItemSurface({
  item,
  packageId,
  onUpdated,
  response,
  onSigningBusyChange,
  onReload,
}: ESignItemSurfaceProps) {
  // The template may opt into in-house signing for an ESign-kind item
  // (rare today; reserved for templates that should later move to DocuSign
  // without churning the portal flow). The server-resolved route decides.
  if (signingRouteFor(item) === "InHouse") {
    if (item.approvalChain && onUpdated) {
      return (
        <ApprovalChainItemSurface
          item={item}
          onUpdated={onUpdated}
          onReload={onReload}
          onSigningBusyChange={onSigningBusyChange}
        />
      );
    }
    return (
      <InHouseSigningSurface
        item={item}
        onUpdated={onUpdated}
        response={response}
        onBusyChange={onSigningBusyChange}
      />
    );
  }
  return (
    <DocuSignSurface item={item} packageId={packageId} onUpdated={onUpdated} response={response} />
  );
}

type PauseReason = "cancel" | "session_timeout" | "ttl_expired";

type ReturnPhase =
  | { kind: "signing" }
  | { kind: "finalizing" }
  | { kind: "finalize-timeout" }
  | { kind: "paused"; reason: PauseReason }
  | { kind: "declined" }
  | { kind: "ended" };

const PAUSED_COPY: Record<PauseReason, { title: string; body: string }> = {
  cancel: {
    title: "Signing paused",
    body: "Your progress is saved. Pick up where you left off whenever you're ready.",
  },
  session_timeout: {
    title: "Signing session timed out",
    body: "Your progress is saved. Reopen the signing window to continue.",
  },
  ttl_expired: {
    title: "Signing window expired",
    body: "The signing window expired before you finished. Your progress is saved — reopen to continue.",
  },
};

function DocuSignSurface({
  item,
  packageId,
  onUpdated,
  response,
}: {
  item: PortalItemDto;
  packageId: string;
  onUpdated?: (next: PortalItemDto) => void;
  response?: PortalResponse | null;
}) {
  const [start, setStart] = useState<ESignStartResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<ReturnPhase>({ kind: "signing" });
  const [refreshing, setRefreshing] = useState(false);

  // Keep the latest callback out of effect deps — ItemPage recreates it per
  // render, and the finalizing poll must not restart on parent re-renders.
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  // Always a recipient action — never fired on mount (A16). When a response
  // is owed the note is required before signing and goes with the start; the
  // server attaches it when DocuSign completes.
  async function handleOpen() {
    if (response?.blockedReason) return;
    setLoading(true);
    setError(null);
    try {
      const result = await portalPackagesApi.startESign(item.id, response?.note);
      response?.onCommitted();
      setStart(result);
      setPhase({ kind: "signing" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open signing. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  // Return-page messages: validate origin + source + item, then tear the
  // iframe down and branch on DocuSign's return event.
  useEffect(() => {
    if (!start) return;
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const data = e.data as { source?: unknown; itemId?: unknown; event?: unknown } | null;
      if (!data || typeof data !== "object") return;
      if (data.source !== ESIGN_RETURN_MESSAGE_SOURCE || data.itemId !== item.id) return;

      const signingEvent = typeof data.event === "string" ? data.event : "";
      setStart(null);
      if (signingEvent === "signing_complete") {
        setPhase({ kind: "finalizing" });
      } else if (
        signingEvent === "cancel" ||
        signingEvent === "session_timeout" ||
        signingEvent === "ttl_expired"
      ) {
        setPhase({ kind: "paused", reason: signingEvent });
      } else if (signingEvent === "decline") {
        setPhase({ kind: "declined" });
      } else {
        // exception / access_code_failed / id_check_failed / viewing_complete
        // / anything DocuSign adds later — generic end card, reopenable.
        setPhase({ kind: "ended" });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [start, item.id]);

  // signing_complete → the Connect webhook is the state authority; poll the
  // package until the item flips (usually seconds), then hand the fresh row
  // up so the under-review card + step indicator render without a reload.
  useEffect(() => {
    if (phase.kind !== "finalizing") return;
    if (!packageId) {
      // Defensive: without a packageId the poll can't run — fall straight to
      // the manual-check card rather than spinning forever.
      setPhase({ kind: "finalize-timeout" });
      return;
    }
    let cancelled = false;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt >= FINALIZE_POLL_CAP_MS) {
        window.clearInterval(timer);
        setPhase({ kind: "finalize-timeout" });
        return;
      }
      void (async () => {
        try {
          const pkg = await portalPackagesApi.get(packageId);
          if (cancelled) return;
          const fresh = pkg.items.find((i) => i.id === item.id);
          if (fresh && signatureLanded(fresh)) {
            window.clearInterval(timer);
            setPhase({ kind: "signing" });
            onUpdatedRef.current?.(fresh);
          }
        } catch {
          // transient fetch failure — keep polling until the cap
        }
      })();
    }, FINALIZE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase.kind, packageId, item.id]);

  async function refreshNow() {
    if (!packageId) return;
    setRefreshing(true);
    setError(null);
    try {
      const pkg = await portalPackagesApi.get(packageId);
      const fresh = pkg.items.find((i) => i.id === item.id);
      if (fresh) {
        if (signatureLanded(fresh)) {
          setPhase({ kind: "signing" });
        }
        onUpdatedRef.current?.(fresh);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't refresh. Try again in a moment.");
    } finally {
      setRefreshing(false);
    }
  }

  const chain = item.approvalChain ?? null;

  // Approval chains — the caller's stage is done (the Connect webhook
  // completed it): say so, and offer the fully signed copy once every party
  // has signed. Same A20 rule: the server's chain view speaks, not a timer.
  if (chain && chain.myStatus === "Done") {
    return (
      <div className="flex flex-col gap-space-4" data-testid="esign-chain-done">
        <ApprovalChainProgress chain={chain} />
        <ChainDoneCard item={item} />
      </div>
    );
  }

  // A20 — "Signature received" is the item's status speaking (the Connect
  // webhook flipped it), never a timer or DocuSign's return event alone.
  if (!chain && (item.status === "SubmittedForReview" || item.status === "Approved")) {
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-received"
      >
        <h2 className="text-h4 font-display text-onyx m-0">Signature received</h2>
        <p className="text-small text-onyx m-0">
          {item.status === "Approved"
            ? "Your signed document has been approved."
            : "Your signed document is being reviewed. We'll let you know if anything else is needed."}
        </p>
      </div>
    );
  }

  if (phase.kind === "finalizing") {
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-finalizing"
      >
        <WaitState label="Finishing up…" message={FINISHING_UP_DETAIL} />
      </div>
    );
  }

  if (phase.kind === "finalize-timeout") {
    // Past the poll cap with no status flip: still claim nothing — the
    // webhook may simply be slow. "Check again" re-reads the status.
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-finalize-timeout"
      >
        <h2 className="text-h4 font-display text-onyx m-0">Finishing up…</h2>
        <p className="text-small text-onyx m-0">{FINISHING_UP_DETAIL}</p>
        <div>
          <Button variant="secondary" onClick={() => void refreshNow()} disabled={refreshing}>
            {refreshing ? "Checking…" : "Check again"}
          </Button>
        </div>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
      </div>
    );
  }

  if (phase.kind === "paused") {
    const copy = PAUSED_COPY[phase.reason];
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-paused"
      >
        <h2 className="text-h4 font-display text-onyx m-0">{copy.title}</h2>
        <p className="text-small text-onyx m-0">{copy.body}</p>
        <div>
          <Button onClick={() => void handleOpen()} disabled={loading || !!response?.blockedReason}>
            {loading ? "Opening…" : "Resume signing"}
          </Button>
        </div>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
      </div>
    );
  }

  if (phase.kind === "declined" && chain) {
    // No restart: the decline returned the document to Phoenix, so this
    // stage cannot be signed until staff revise it and the chain restarts.
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-declined"
      >
        <h2 className="text-lead font-display text-onyx m-0">You declined to sign</h2>
        <p className="text-small text-onyx m-0">{CHAIN_DECLINE_DETAIL}</p>
      </div>
    );
  }

  if (phase.kind === "declined") {
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-declined"
      >
        <h2 className="text-h4 font-display text-onyx m-0">You declined to sign</h2>
        <p className="text-small text-onyx m-0">
          We've recorded that you declined this document and our team will follow up. If you
          declined by mistake, you can restart signing.
        </p>
        <div>
          {/* Restart creates a fresh envelope once the decline has been
              recorded server-side; if that record is still in flight the
              start call fails with a friendly retryable message. */}
          <Button onClick={() => void handleOpen()} disabled={loading || !!response?.blockedReason}>
            {loading ? "Opening…" : "Restart signing"}
          </Button>
        </div>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
      </div>
    );
  }

  if (phase.kind === "ended") {
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="esign-ended"
      >
        <h2 className="text-h4 font-display text-onyx m-0">The signing session ended unexpectedly</h2>
        <p className="text-small text-onyx m-0">
          You can reopen the signing window and try again. If this keeps happening, contact
          support.
        </p>
        <div>
          <Button onClick={() => void handleOpen()} disabled={loading || !!response?.blockedReason}>
            {loading ? "Opening…" : "Reopen signing window"}
          </Button>
        </div>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
      </div>
    );
  }

  // Only an item that is still with the recipient can be signed.
  if (item.status === "Waived") return null;

  // Approval chains — not the caller's turn (or the chain stopped): say whose
  // turn it is and offer nothing; the server would refuse an envelope.
  if (chain && !chain.canAct) {
    return (
      <div className="flex flex-col gap-space-4">
        <ApprovalChainProgress chain={chain} />
        <p
          className="text-small text-onyx m-0 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="chain-waiting"
        >
          {chain.statusLabel}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      {chain && !start && <ApprovalChainProgress chain={chain} />}
      {!start && (
        <div className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure">
          <h2 className="text-h4 font-display text-onyx m-0">Sign this document</h2>
          <p className="text-small text-onyx m-0">
            Press Start signing to open the signing window here. Your progress saves
            automatically.
          </p>
          <div>
            <Button
              onClick={() => void handleOpen()}
              disabled={loading || !!response?.blockedReason}
              data-testid="esign-start"
            >
              {loading ? "Opening…" : "Start signing"}
            </Button>
          </div>
          <ResponseBlockedHint response={response} />
          {loading && (
            <WaitState
              label="Opening the signing window…"
              message="We're preparing your document with DocuSign. This can take up to a minute — please don't close this tab."
            />
          )}
          {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
        </div>
      )}
      {start && (
        <div className="flex flex-col gap-space-2">
          <iframe
            title="DocuSign signing"
            src={start.recipientViewUrl}
            className="w-full border border-border-light rounded-md"
            style={{ height: "min(720px, 75dvh)" }}
            data-testid="docusign-iframe"
          />
          <p className="text-caption text-gray-mid m-0">
            Signed by {item.eSignProvider ?? "DocuSign"}.
          </p>
        </div>
      )}
    </div>
  );
}
